import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .ew-root { font-family: 'Plus Jakarta Sans', sans-serif; }
    .ew-zh   { font-family: 'Fraunces', Georgia, serif; }

    @keyframes ew-bd   { from{opacity:0} to{opacity:1} }
    @keyframes ew-card { from{opacity:0;transform:translateY(22px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }

    .ew-bd-anim   { animation: ew-bd   .2s both; }
    .ew-card-anim { animation: ew-card .32s cubic-bezier(.22,.68,0,1.2) both; }

    /* ── Input ── */
    .ew-input {
      width:100%; border:1.5px solid #e8e8e4;
      border-radius:11px; padding:12px 48px 12px 48px;
      font-size:15px; font-family:'Plus Jakarta Sans',sans-serif;
      color:#111; background:#fafaf9; outline:none;
      transition:border-color .15s, background .15s, box-shadow .15s;
      letter-spacing:.04em;
    }
    .ew-input::placeholder { color:#c0c0ba; letter-spacing:0; }
    .ew-input:focus {
      border-color:#6cbb23; background:#fff;
      box-shadow:0 0 0 3px rgba(108,187,35,.1);
    }
    .ew-input:hover:not(:focus) { border-color:#d0d0ca; }

    /* ── Submit ── */
    .ew-submit {
      width:100%; border:none; background:#6cbb23; color:#fff;
      border-radius:11px; padding:13px;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:14.5px; font-weight:700;
      cursor:pointer; position:relative; overflow:hidden;
      transition:transform .18s, box-shadow .18s;
    }
    .ew-submit::after {
      content:''; position:absolute; inset:0;
      background:rgba(255,255,255,.13); opacity:0; transition:opacity .18s;
    }
    .ew-submit:hover { transform:translateY(-2px); box-shadow:0 10px 30px rgba(108,187,35,.38); }
    .ew-submit:hover::after { opacity:1; }
    .ew-submit:active { transform:scale(.97); }

    /* ── Close ── */
    .ew-close {
      position:absolute; top:14px; right:14px;
      width:30px; height:30px; border-radius:8px;
      border:1.5px solid rgba(255,255,255,.25);
      background:rgba(255,255,255,.1);
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; z-index:20;
      transition:background .15s, border-color .15s;
    }
    .ew-close:hover { background:rgba(255,255,255,.22); border-color:rgba(255,255,255,.4); }

    /* ── Cancel text btn ── */
    .ew-cancel {
      display:block; width:100%; margin-top:10px;
      background:none; border:none; cursor:pointer;
      font-size:12.5px; color:#bbb;
      font-family:'Plus Jakarta Sans',sans-serif; font-weight:500; padding:6px 0;
      transition:color .15s;
    }
    .ew-cancel:hover { color:#888; }

    /* ── Header grid texture (pointer-none so it never blocks clicks) ── */
    .ew-header-bg {
      position:absolute; inset:0; pointer-events:none;
      background-image:
        linear-gradient(rgba(108,187,35,.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(108,187,35,.05) 1px, transparent 1px);
      background-size:32px 32px;
    }
    .ew-header-glow {
      position:absolute; top:-40%; left:50%; transform:translateX(-50%);
      width:320px; height:200px; border-radius:50%; pointer-events:none;
      background:radial-gradient(ellipse, rgba(108,187,35,.16) 0%, transparent 70%);
    }

    /* ── Decorative corner lines — always pointer-none ── */
    .ew-corner {
      position:absolute; width:14px; height:14px; pointer-events:none;
    }
    .ew-corner::before, .ew-corner::after {
      content:''; position:absolute; background:rgba(255,255,255,.15);
    }
    .ew-corner::before { left:50%; top:0; bottom:0; width:1.5px; transform:translateX(-50%); }
    .ew-corner::after  { top:50%; left:0; right:0; height:1.5px; transform:translateY(-50%); }
  `}</style>
);

/* ─────────────────────────────────────────────
   WHATSAPP ICON — self-contained, fixed size
───────────────────────────────────────────── */
const WAIcon = ({ size = 20, color = '#6cbb23' }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display:'block', flexShrink:0 }}
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12c0 1.77.47 3.43 1.28 4.87L2 22l5.26-1.26A9.93 9.93 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"
      fill={color}
    />
    <path
      d="M9.23 8.03c-.24-.54-.5-.55-.72-.56H8.1c-.2 0-.53.07-.8.37-.28.3-1.06 1.03-1.06 2.52 0 1.49 1.08 2.93 1.24 3.13.15.2 2.1 3.33 5.17 4.53 2.56.99 3.08.8 3.64.75.56-.06 1.8-.74 2.06-1.45.26-.71.26-1.32.18-1.45-.08-.13-.3-.2-.63-.37-.34-.17-2-.98-2.3-1.1-.31-.11-.53-.17-.76.18-.22.35-.87 1.1-1.06 1.32-.2.23-.4.26-.73.09-.34-.18-1.44-.53-2.74-1.7-1.01-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.53.15-.7.15-.15.34-.4.51-.59.17-.2.23-.34.34-.57z"
      fill="#fff"
    />
  </svg>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT — LOGIC UNCHANGED
───────────────────────────────────────────── */
const EditWhatsappModal = ({ isOpen, onClose, email }) => {
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length > 12) return;
    if (!/^\d*$/.test(value)) return;
    setWhatsappNumber(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!whatsappNumber) { toast.error('WhatsApp Number is required!'); return; }
    if (whatsappNumber.length !== 12) { toast.error('WhatsApp Number must be exactly 12 digits!'); return; }
    try {
      const userDocRef = doc(db, 'userDetails', email);
      await updateDoc(userDocRef, { whatsappNumber });
      toast.success('WhatsApp Number updated successfully!');
      setWhatsappNumber('');
      onClose();
    } catch (error) {
      console.error('Error updating WhatsApp Number', error);
      toast.error('Failed to update WhatsApp Number.');
    }
  };

  if (!isOpen) return null;

  /* Progress ring maths */
  const r    = 13;
  const circ = 2 * Math.PI * r;
  const dash = circ - (whatsappNumber.length / 12) * circ;
  const done = whatsappNumber.length === 12;

  return (
    <>
      <Styles />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Backdrop */}
      <div
        className="ew-bd-anim ew-root"
        onClick={onClose}
        style={{
          position:'fixed', inset:0, zIndex:50,
          background:'rgba(8,12,8,.72)',
          backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          display:'flex', alignItems:'center', justifyContent:'center',
          padding:16,
        }}
      >
        {/* Card */}
        <div
          className="ew-card-anim"
          onClick={e => e.stopPropagation()}
          style={{
            width:'100%', maxWidth:400,
            background:'#fff', borderRadius:22, overflow:'hidden',
            boxShadow:'0 32px 72px rgba(0,0,0,.3), 0 4px 14px rgba(0,0,0,.1)',
          }}
        >

          {/* ── DARK HEADER ── */}
          <div style={{
            background:'linear-gradient(160deg,#0c1a0c 0%,#152815 60%,#0a120a 100%)',
            padding:'22px 24px 22px',
            position:'relative', overflow:'hidden', minHeight:130,
          }}>
            {/* decorative — all pointer-events:none so close button is never blocked */}
            <div className="ew-header-bg" />
            <div className="ew-header-glow" />
            <div className="ew-corner" style={{ top:12, left:16 }} />
            {/* right corner stays away from close button — left side only */}
            <div className="ew-corner" style={{ bottom:12, right:16 }} />

            {/* Close button — z-index:20 sits above all decorative layers */}
            <button
              className="ew-close"
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1L1 9" stroke="rgba(255,255,255,.75)" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>

            {/* Content */}
            <div style={{ position:'relative', zIndex:2 }}>
              {/* Icon chip + label row */}
              <div style={{ display:'flex', alignItems:'center', gap:11, marginBottom:14 }}>
                {/* Icon chip — fixed 40×40, icon centered inside */}
                <div style={{
                  width:40, height:40, borderRadius:12, flexShrink:0,
                  background:'rgba(108,187,35,.18)', border:'1.5px solid rgba(108,187,35,.3)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  overflow:'hidden',
                }}>
                  <WAIcon size={22} color="#6cbb23" />
                </div>

                <div>
                  <div style={{
                    fontSize:10, fontWeight:700, color:'rgba(108,187,35,.85)',
                    letterSpacing:'.1em', textTransform:'uppercase',
                    fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:3,
                  }}>
                    WhatsApp Settings
                  </div>
                  <h2 className="ew-zh" style={{
                    fontSize:'clamp(17px,2.2vw,21px)', fontWeight:900, fontStyle:'italic',
                    color:'#fff', lineHeight:1.1, letterSpacing:'-0.02em', margin:0,
                  }}>
                    Update WhatsApp Number
                  </h2>
                </div>
              </div>

              <p style={{
                fontSize:12, color:'rgba(255,255,255,.38)', fontWeight:300,
                lineHeight:1.6, margin:0, maxWidth:320,
              }}>
                This number receives all your order notifications, bookings, and customer alerts.
              </p>
            </div>
          </div>

          {/* ── FORM BODY ── */}
          <form onSubmit={handleSubmit} style={{ padding:'22px 24px 24px' }}>

            {/* Label */}
            <label style={{
              display:'block', fontSize:10, fontWeight:700, color:'#b0b0aa',
              letterSpacing:'.07em', textTransform:'uppercase', marginBottom:6,
            }}>
              WhatsApp Number
            </label>

            {/* Input wrapper */}
            <div style={{ position:'relative', marginBottom:8 }}>

              {/* Left: WA icon + divider */}
              <div style={{
                position:'absolute', left:0, top:0, bottom:0, width:44,
                display:'flex', alignItems:'center', justifyContent:'center',
                pointerEvents:'none', zIndex:1,
              }}>
                <WAIcon size={17} color="#6cbb23" />
                <div style={{ position:'absolute', right:0, top:'25%', bottom:'25%', width:1, background:'#e0e0da' }} />
              </div>

              <input
                type="text"
                name="whatsappNumber"
                value={whatsappNumber}
                onChange={handleChange}
                className="ew-input"
                placeholder="91XXXXXXXXXX"
                autoComplete="off"
              />

              {/* Right: SVG progress ring */}
              <div style={{
                position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                pointerEvents:'none', lineHeight:0,
              }}>
                <svg width="30" height="30" viewBox="0 0 30 30">
                  <circle cx="15" cy="15" r={r} fill="none" stroke="#f0f0ec" strokeWidth="2.2"/>
                  {whatsappNumber.length > 0 && (
                    <circle
                      cx="15" cy="15" r={r} fill="none"
                      stroke={done ? '#6cbb23' : '#6cbb23'}
                      strokeWidth="2.2"
                      strokeDasharray={circ}
                      strokeDashoffset={dash}
                      strokeLinecap="round"
                      transform="rotate(-90 15 15)"
                      style={{ transition:'stroke-dashoffset .18s' }}
                    />
                  )}
                  <text
                    x="15" y="19.5" textAnchor="middle"
                    fontSize="8.5" fontWeight="700"
                    fill={done ? '#6cbb23' : '#b0b0aa'}
                    fontFamily="'Plus Jakarta Sans',sans-serif"
                  >
                    {whatsappNumber.length === 0 ? '12' : whatsappNumber.length}
                  </text>
                </svg>
              </div>
            </div>

            {/* Helper */}
            <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:16 }}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <circle cx="5.5" cy="5.5" r="4.5" stroke="#c8c8c4" strokeWidth="1"/>
                <path d="M5.5 4v2.5M5.5 7.5h.01" stroke="#c8c8c4" strokeWidth="1.1" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize:11, color:'#bbb', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                Country code first · e.g. <strong style={{ color:'#999', fontWeight:600 }}>91</strong>9876543210
              </span>
            </div>

            {/* Info notice */}
            <div style={{
              display:'flex', alignItems:'flex-start', gap:10,
              padding:'10px 13px', marginBottom:18,
              background:'#f8fdf4', border:'1.5px solid #cdeaa0',
              borderRadius:11,
            }}>
              <div style={{
                width:22, height:22, borderRadius:6, flexShrink:0, marginTop:1,
                background:'rgba(108,187,35,.15)', border:'1px solid rgba(108,187,35,.28)',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <WAIcon size={12} color="#6cbb23" />
              </div>
              <p style={{
                fontSize:11.5, color:'#5a8c1a', lineHeight:1.55, margin:0,
                fontFamily:"'Plus Jakarta Sans',sans-serif",
              }}>
                Order notifications and customer messages will be delivered to this number right away.
              </p>
            </div>

            {/* Submit */}
            <button type="submit" className="ew-submit">
              Save & Update Number
            </button>

            {/* Cancel */}
            <button type="button" onClick={onClose} className="ew-cancel">
              Cancel
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default EditWhatsappModal;